/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Col, Input, message, Row, Select, Typography } from 'antd';
import { useIntl } from 'react-intl';
import {
  Fragment,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { queryCache, useMutation } from 'react-query';
import {
  addTenderContacts,
  patchTendersRegistrationInfo,
  putTenderContractor,
} from '~src/services/api';
import { appLog } from '~src/utils/log';
import { IconPopup } from '~components/popups-modals/DetailsLayouts/components';
import { useProjectContacts } from '~src/hooks/api-hooks/useProjectContacts';

export type ContractorEditTabRef = {
  handleSave: () => void;
  isLoading: boolean;
};

export const ContractorEditTab = forwardRef<
  ContractorEditTabRef,
  {
    visible?: boolean;
    item: {
      title: string | undefined;
      reference?: string;
      projectId?: string;
      tenderId?: string;
      orderGiver?: {
        contactor_title?: string;
        contactor_subtitle?: string;
      };
      location?: {
        location_1?: string;
        location_2?: string;
      };
      contractorCompany?: string;
      contact?: string[];
    };
    onOk?: () => void; // called when save succeeds
    onLoadingChange?: (loading: boolean) => void; // used by parent modal confirmLoading
  }
>(({ item, visible, onOk, onLoadingChange }, ref) => {
  const intl = useIntl();
  const [isModalLoading, setIsModalLoading] = useState(false);

  const availableContacts = useProjectContacts(item.projectId, {
    enabled: !!item.projectId,
  });

  const [orderGiver, setOrderGiver] = useState<typeof item.orderGiver>({});
  const [location, setLocation] = useState<typeof item.location>({});
  const [contractorCompany, setContractorCompany] = useState<
    string | undefined
  >();
  const [contactPerson, setContactPerson] = useState<string[] | undefined>();

  // propagate loading to parent modal (for confirmLoading)
  useEffect(() => {
    onLoadingChange?.(isModalLoading);
  }, [isModalLoading, onLoadingChange]);

  const didInitRef = useRef(false);

  // Only reset form when modal opens, not when data refetches
  // This prevents React Query's 5-minute refetch interval from clearing user input
  useEffect(() => {
    // init once per open (fix for "empty on first open" when component mounts while visible === true)
    if (visible && !didInitRef.current) {
      // Modal just opened - initialize form with current data
      setOrderGiver(item.orderGiver);
      setLocation(item.location);
      setContractorCompany(item.contractorCompany);
      setContactPerson(item.contact);

      didInitRef.current = true;
    }

    // reset init flag when modal closes (so next open re-inits)
    if (!visible) {
      didInitRef.current = false;
    }
  }, [
    visible,
    item.orderGiver,
    item.location,
    item.contractorCompany,
    item.contact,
  ]);

  const [doSaveTenderBuyerData] = useMutation(patchTendersRegistrationInfo, {
    onMutate: () => {
      setIsModalLoading(true);
    },
    onError: (err) => {
      appLog('err -> ', err);
      // message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
      message.error(
        intl.formatMessage({ id: 'notification-error-order-giver' })
      );
      setIsModalLoading(false);
    },
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
      queryCache.invalidateQueries('tendersRegistrationCoreTenders');
      setIsModalLoading(false);

      onOk && onOk();
    },
  });

  const [doSaveTenderContactsData] = useMutation(addTenderContacts, {
    onMutate: () => {
      setIsModalLoading(true);
    },
    onError: (err) => {
      appLog('err -> ', err);
      // message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
      message.error(
        intl.formatMessage({ id: 'notification-error-contacts-data' })
      );
      setIsModalLoading(false);
    },
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
      queryCache.invalidateQueries('tendersRegistrationCoreTenders');
      setIsModalLoading(false);

      onOk && onOk();
    },
  });

  const [doSaveTenderContractorData] = useMutation(putTenderContractor, {
    onMutate: () => {
      setIsModalLoading(true);
    },
    onError: (err) => {
      appLog('err -> ', err);
      // message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
      message.error(
        intl.formatMessage({ id: 'notification-error-contractor-data' })
      );
      setIsModalLoading(false);
    },
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
      queryCache.invalidateQueries('tendersRegistrationCoreTenders');
      setIsModalLoading(false);

      onOk && onOk();
    },
  });

  function handleSave() {
    if (!item.projectId || !item.tenderId) return;

    doSaveTenderBuyerData({
      projectId: item.projectId,
      tenderId: item.tenderId,
      contractor_title: orderGiver?.contactor_title,
      contractor_subtitle: orderGiver?.contactor_subtitle,
      location_1: location?.location_1,
      location_2: location?.location_2,
    } as Parameters<typeof doSaveTenderBuyerData>[0]);

    if (
      contactPerson &&
      contactPerson.length &&
      item.contact?.[0] !== contactPerson[0]
    ) {
      doSaveTenderContactsData({
        projectId: item.projectId,
        tenderId: item.tenderId,
        contact_keys: contactPerson,
        temp_old_keys: item.contact,
      });
    }

    if (contractorCompany && item.contractorCompany != contractorCompany) {
      doSaveTenderContractorData({
        projectId: item.projectId,
        tenderId: item.tenderId,
        contact_key: contractorCompany,
      });
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      handleSave,
      isLoading: isModalLoading,
    }),
    [isModalLoading, contractorCompany, contactPerson, orderGiver, location]
  );

  const ContractorDetails = ({ contact_key }: { contact_key?: string }) => {
    const contact = availableContacts?.find(
      (c) => c.contact_key === contact_key
    );

    if (!availableContacts || !contact) return <Fragment></Fragment>;

    return (
      <Fragment>
        <label style={{ fontWeight: 700 }}>
          {contact.company_title ?? contact.title}
        </label>
        <label style={{ fontWeight: 700 }}>{contact.location_1 ?? ''}</label>
        <label style={{ fontWeight: 700 }}>{contact.address_1 ?? ''}</label>
        <label style={{ fontWeight: 700 }}>{contact.email_1 ?? ''}</label>
        <label style={{ fontWeight: 700 }}>{contact.phone_1 ?? ''}</label>
      </Fragment>
    );
  };

  const ContactsDetails = ({ contact_key }: { contact_key?: string[] }) => {
    const contact = availableContacts?.filter((c) =>
      contact_key?.includes(c.contact_key)
    );

    if (!availableContacts || !contact || !contact.length)
      return <Fragment></Fragment>;

    return (
      <Fragment>
        <label style={{ fontWeight: 700 }}>
          {contact[0].name_1 ?? contact[0].company_title}
        </label>
        <label style={{ fontWeight: 700 }}>{contact[0].location_1 ?? ''}</label>
        <label style={{ fontWeight: 700 }}>{contact[0].address_1 ?? ''}</label>
        <label style={{ fontWeight: 700 }}>{contact[0].email_1 ?? ''}</label>
        <label style={{ fontWeight: 700 }}>{contact[0].phone_1 ?? ''}</label>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '5px',
        }}>
        <Row style={{ display: 'flex', flex: 1 }}>
          <Col span={6}>
            {item.reference ? (
              <Typography>
                {intl.formatMessage({ id: 'contactor-ref-number' })}
              </Typography>
            ) : null}
            {item.title ? (
              <Typography>
                {intl.formatMessage({ id: 'tender-name' })}
              </Typography>
            ) : null}
          </Col>
          <Col>
            {item.reference ? (
              <Typography style={{ fontWeight: 700 }}>
                {item.reference ?? intl.formatMessage({ id: 'not-found-id' })}
              </Typography>
            ) : null}
            {item.title ? (
              <Typography style={{ fontWeight: 700 }}>
                {item.title?.slice(0, 50) ??
                  intl.formatMessage({ id: 'not-found-title' })}
              </Typography>
            ) : null}
          </Col>
        </Row>
        <IconPopup
          headerId={'#1014-header'}
          messageId={'#1014-message'}
          customStyle={{ fontSize: '130%' }}
        />
      </Row>

      <Typography style={{ fontSize: '16px', fontWeight: 700, marginTop: 10 }}>
        {intl.formatMessage({ id: 'buyer' })}
      </Typography>
      <label style={{ fontSize: '14px' }}>
        {intl.formatMessage({ id: 'contactor-title' })}
      </label>
      <Input
        autoCapitalize="words"
        value={orderGiver?.contactor_title}
        onChange={({ target: { value } }) =>
          setOrderGiver((p) => ({ ...p, contactor_title: value }))
        }
      />
      <label style={{ fontSize: '14px' }}>
        {intl.formatMessage({ id: 'contactor-subtitle' })}
      </label>
      <Input
        autoCapitalize="words"
        value={orderGiver?.contactor_subtitle}
        onChange={({ target: { value } }) =>
          setOrderGiver((p) => ({ ...p, contactor_subtitle: value }))
        }
      />

      <Typography style={{ fontSize: '16px', fontWeight: 700, marginTop: 10 }}>
        {intl.formatMessage({ id: 'location' })}
      </Typography>
      <label style={{ fontSize: '14px' }}>
        {intl.formatMessage({ id: 'location-first' })}
      </label>
      <Input
        autoCapitalize="words"
        value={location?.location_1}
        onChange={({ target: { value } }) =>
          setLocation((p) => ({ ...p, location_1: value }))
        }
      />
      <label style={{ fontSize: '14px' }}>
        {intl.formatMessage({ id: 'location-second' })}
      </label>
      <Input
        autoCapitalize="words"
        value={location?.location_2}
        onChange={({ target: { value } }) =>
          setLocation((p) => ({ ...p, location_2: value }))
        }
      />

      <Typography style={{ fontSize: '16px', fontWeight: 700, marginTop: 10 }}>
        {intl.formatMessage({ id: 'contactor-company' })}
      </Typography>
      <Row
        style={{
          display: 'flex',
          flex: 1,
          alignSelf: 'stretch',
          justifyContent: 'space-between',
        }}>
        {availableContacts && availableContacts.length ? (
          <Fragment>
            <Col span={12}>
              <Select
                style={{ width: '100%' }}
                onChange={setContractorCompany}
                placeholder={intl.formatMessage({ id: 'list-select-from' })}
                value={contractorCompany}>
                <Select.OptGroup label={intl.formatMessage({ id: 'business' })}>
                  {availableContacts
                    .filter((c) => +(c.type ?? -1) >= 2000)
                    .map((c) => (
                      <Select.Option key={c.contact_key} value={c.contact_key}>
                        {c.title ?? c.name_1 ?? c.company_title}
                      </Select.Option>
                    ))}
                </Select.OptGroup>
                <Select.OptGroup label={intl.formatMessage({ id: 'personal' })}>
                  {availableContacts
                    .filter(
                      (c) => +(c.type ?? -1) >= 1000 && +(c.type ?? -1) < 2000
                    )
                    .map((c) => (
                      <Select.Option key={c.contact_key} value={c.contact_key}>
                        {c.title ?? c.name_1 ?? c.company_title}
                      </Select.Option>
                    ))}
                </Select.OptGroup>
              </Select>
            </Col>
            <Col span={10} style={{ display: 'flex', flexDirection: 'column' }}>
              <ContractorDetails contact_key={contractorCompany} />
            </Col>
          </Fragment>
        ) : (
          <label>{intl.formatMessage({ id: 'no-data' })}</label>
        )}
      </Row>

      <Typography style={{ fontSize: '16px', fontWeight: 700, marginTop: 10 }}>
        {intl.formatMessage({ id: 'contactor-person' })}
      </Typography>
      <Row
        style={{
          display: 'flex',
          flex: 1,
          alignSelf: 'stretch',
          justifyContent: 'space-between',
        }}>
        {availableContacts && availableContacts.length ? (
          <Fragment>
            <Col span={12}>
              <Select
                style={{ width: '100%' }}
                onChange={(value) => setContactPerson([value])}
                placeholder={intl.formatMessage({ id: 'list-select-from' })}
                value={contactPerson?.[0]}>
                <Select.OptGroup label={intl.formatMessage({ id: 'business' })}>
                  {availableContacts
                    .filter((c) => +(c.type ?? -1) >= 2000)
                    .map((c) => (
                      <Select.Option key={c.contact_key} value={c.contact_key}>
                        {c.title ?? c.name_1 ?? c.company_title}
                      </Select.Option>
                    ))}
                </Select.OptGroup>
                <Select.OptGroup label={intl.formatMessage({ id: 'personal' })}>
                  {availableContacts
                    .filter(
                      (c) => +(c.type ?? -1) >= 1000 && +(c.type ?? -1) < 2000
                    )
                    .map((c) => (
                      <Select.Option key={c.contact_key} value={c.contact_key}>
                        {c.title ?? c.name_1 ?? c.company_title}
                      </Select.Option>
                    ))}
                </Select.OptGroup>
              </Select>
            </Col>
            <Col span={10} style={{ display: 'flex', flexDirection: 'column' }}>
              <ContactsDetails contact_key={contactPerson} />
            </Col>
          </Fragment>
        ) : (
          <label>{intl.formatMessage({ id: 'no-data' })}</label>
        )}
      </Row>
    </Fragment>
  );
});
